import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@aslanpay/portal-common-lib';

import { RECONCILIATION_FINDINGS_QUERY_KEY } from 'app/hooks/useFetchReconciliationFindings';

import ReconciliationsApiHandler from 'app/services/reconciliationsApiHandler';

import {
  AcceptReconciliationFindingRequest,
  ReconciliationFinding,
} from 'app/types/reconciliations';

const DEFAULT_ERROR_DESCRIPTION = 'Sorry, the problem could not be accepted. Please try again.';

const getErrorDescription = (error: Error) =>
  (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  DEFAULT_ERROR_DESCRIPTION;

/** Accepts an open or past problem as known, with a note saying why. */
const useAcceptReconciliationFinding = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<ReconciliationFinding, Error, AcceptReconciliationFindingRequest>(
    (request) => ReconciliationsApiHandler.acceptFinding(request),
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Problem accepted' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: [RECONCILIATION_FINDINGS_QUERY_KEY] }),
    }
  );
};

export default useAcceptReconciliationFinding;
